import React, { useMemo, useState, useEffect } from "react";
import "../../starek.css";

function ZoneHierarchicalView({
    zones,
    onAddZone,
    onEditZone,
    selectedRows,
    onSelectedRowsChange,
}) {
    const [internalSelectedRows, setInternalSelectedRows] = useState(
        selectedRows || []
    );

    // Sync internal state with parent prop
    useEffect(() => {
        setInternalSelectedRows(selectedRows || []);
    }, [selectedRows]);

    const isZoneSelected = (zoneId) => {
        return internalSelectedRows.some((row) => row.id === zoneId);
    };

    const handleCheckboxChange = (zone) => {
        let newSelectedRows;
        if (isZoneSelected(zone.id)) {
            newSelectedRows = internalSelectedRows.filter(
                (row) => row.id !== zone.id
            );
        } else {
            newSelectedRows = [...internalSelectedRows, zone];
        }
        setInternalSelectedRows(newSelectedRows);
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };
    // Build children map
    const childrenMap = useMemo(() => {
        const map = new Map();
        zones.forEach((zone) => {
            if (!map.has(zone.parent_id)) {
                map.set(zone.parent_id, []);
            }
            map.get(zone.parent_id).push(zone);
        });
        return map;
    }, [zones]);

    const renderZone = (zone, depth = 0) => {
        const children = childrenMap.get(zone.id) || [];
        const deviceCount =
            zone._count?.installed_devices ||
            zone.installed_devices?.length ||
            0;

        const isRoot = depth === 0;
        const isSelected = isZoneSelected(zone.id);

        return (
            <div
                key={zone.id}
                className={`zone-box ${
                    isRoot ? "zone-box-root" : "zone-box-child"
                }`}
            >
                <div className="zone-header">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                flex: 1,
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleCheckboxChange(zone)}
                                style={{
                                    cursor: "pointer",
                                    width: "18px",
                                    height: "18px",
                                }}
                            />
                            <h3
                                className="zone-name"
                                onClick={() =>
                                    onEditZone && onEditZone(zone.id)
                                }
                                style={{
                                    cursor: onEditZone ? "pointer" : "default",
                                }}
                            >
                                {zone.name}
                            </h3>
                        </div>
                        {onAddZone && (
                            <button
                                className="zone-add-btn"
                                onClick={() => onAddZone(zone.id)}
                                title="Add child zone"
                            >
                                +
                            </button>
                        )}
                    </div>
                </div>
                <div className="zone-content">
                    {zone.description && (
                        <div className="zone-description">
                            <small className="text-muted">
                                {zone.description}
                            </small>
                        </div>
                    )}
                    <div className="zone-info">
                        <span className="device-count">
                            {deviceCount} device{deviceCount !== 1 ? "s" : ""}
                        </span>
                    </div>
                    {children.length > 0 && (
                        <div className="zone-children-container">
                            {children.map((child) =>
                                renderZone(child, depth + 1)
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const rootZones = useMemo(
        () => zones.filter((z) => z.parent_id === null),
        [zones]
    );

    if (rootZones.length === 0) {
        return (
            <div className="zone-hierarchical-view">
                <div className="org-chart-container">
                    <div className="zones-boundary">
                        <div className="zone-empty-state">
                            <p className="zone-empty-message">
                                No zones have been created yet. Get started by{" "}
                                <button
                                    className="zone-empty-action"
                                    onClick={() => {
                                        // Pass null to indicate root zone creation
                                        if (onAddZone) {
                                            onAddZone(null);
                                        }
                                    }}
                                >
                                    adding your first zone
                                </button>
                                .
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="zone-hierarchical-view">
            <div className="org-chart-container">
                <div className="zones-boundary">
                    <div className="root-zones-wrapper">
                        {rootZones.map((zone) => renderZone(zone, 0))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ZoneHierarchicalView;
